import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import type { BandData, TimeEvent } from "../../band-data/types";
import { useBandData } from "../../band-data/use-band-data";
import type { BandSpec, TimeAxisPanelSpec } from "../../ChartSpec";
import { useDataViewerStore } from "../../data-viewer-provider";
import type { BandHighlight, PanelBandOptions } from "../build-time-panel-options";
import { MIN_HIT_PX } from "./band-geometry";
import { totalMs } from "./band-totals";
import { findSpansAt } from "./spans-at-time";

const NO_EVENTS: TimeEvent[] = [];
const NO_SPANS: ReturnType<typeof findSpansAt> = {};
const NO_HIGHLIGHT: BandHighlight = { scheduled: NO_EVENTS, actual: NO_EVENTS };

export type PanelBand = PanelBandOptions & {
    plotHighlight: NonNullable<BandSpec["plotHighlight"]>;
    totals: { scheduledMs: number; actualMs: number };
    isError: boolean;
};

export type BandHover = { hovered: ReturnType<typeof findSpansAt>; highlight: BandHighlight };

/** The band's events and placement, or undefined when the spec has no band. Drawn even with no events, so the card's height never depends on the data. */
export function usePanelBand(
    spec: TimeAxisPanelSpec,
    bandData: BandData | undefined,
    window: { startMs: number; endMs: number },
): PanelBand | undefined {
    const { startMs, endMs } = window;
    // Only the first band is drawn.
    const bandSpec = spec.bands?.[0];
    const position = bandSpec?.position ?? "below";
    const plotHighlight = bandSpec?.plotHighlight ?? "hover";

    const fetched = useBandData(bandData ? undefined : bandSpec, startMs, endMs);
    const shown = bandData ?? fetched;
    const scheduled = shown?.scheduled ?? NO_EVENTS;
    const actual = shown?.actual ?? NO_EVENTS;
    const isError = shown?.isError === true;

    const panelBand = useMemo<PanelBand>(() => {
        const nowMs = Date.now();
        return {
            scheduled,
            actual,
            position,
            plotHighlight,
            nowMs,
            totals: {
                scheduledMs: totalMs(scheduled, { startMs, endMs }, nowMs),
                actualMs: totalMs(actual, { startMs, endMs }, nowMs),
            },
            isError,
        };
    }, [scheduled, actual, position, plotHighlight, startMs, endMs, isError]);

    return bandSpec !== undefined ? panelBand : undefined;
}

/** The run under the pointer and what to shade. Needs the plot's width, so the panel calls it after layout. */
export function useBandHover(
    panelBand: PanelBand | undefined,
    window: { startMs: number; endMs: number },
    plotWidthPx: number,
): BandHover {
    const scheduled = panelBand?.scheduled ?? NO_EVENTS;
    const actual = panelBand?.actual ?? NO_EVENTS;
    const minHitMs = (MIN_HIT_PX * (window.endMs - window.startMs)) / Math.max(plotWidthPx, 1);

    const hovered = useDataViewerStore(
        useShallow((state) =>
            state.hoverTarget === "band" && state.timestamp !== null
                ? findSpansAt(scheduled, actual, state.timestamp, minHitMs)
                : NO_SPANS,
        ),
    );

    return useMemo(() => {
        if (!panelBand) return { hovered, highlight: NO_HIGHLIGHT };
        const highlight =
            panelBand.plotHighlight === "always"
                ? { scheduled, actual }
                : {
                      scheduled: hovered.scheduled ? [hovered.scheduled] : [],
                      actual: hovered.actual ? [hovered.actual] : [],
                  };
        return { hovered, highlight };
    }, [panelBand, scheduled, actual, hovered]);
}
